import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { Users, Pencil, X, Check, FileSignature, ShieldCheck, ArrowRightLeft } from 'lucide-react';
import {
  adoptLenderSharesAsHolders, apiErrorMessage, approveHolderChange, getFacilityHolders, getLenderShareCandidates, getPendingHolderChange,
  proposeHolderChange, rejectHolderChange, type HolderChangePending, type LenderShareCandidates,
} from '../api/abfApi';
import type { ActionFeedbackState } from '../components/ActionFeedback';
import type { FacilityHolders } from '../types/funding';
import { fmtDate, fmtMoney, fmtPct, humanizeToken } from '../presentation/format';

/**
 * WS3 — who holds the facility (Oaktree row 5.3). Shares are fixed at acquisition
 * (D-1) and change only by amendment. Fix list 3: a maker proposes the new shares and
 * sees the transfers they imply on a drawn facility; a checker approves, the shares
 * change and the drawn balance moves between the funds at the mark, so every position
 * matches its share. Availability stays a facility fact (D-3).
 */

interface Props {
  facilityId: number;
  currency: string;
  canMake: boolean;
  canCheck: boolean;
  onFeedback: (feedback: ActionFeedbackState) => void;
}

const Card = styled.section`
  margin-bottom: 14px; padding: 1rem; border: 1px solid rgba(148,163,184,.16); border-radius: 12px; background: rgba(8,16,30,.8);
  h2 { margin: 0; color: #f8fafc; font-size: 15px; }
  p.sub { margin: .3rem 0 0; color: #94a3b8; font-size: 12px; }
  table { width: 100%; border-collapse: collapse; margin-top: .8rem; font-size: 12px; font-variant-numeric: tabular-nums; }
  th { text-align: left; padding: .45rem .5rem; color: #7dd3fc; font-size: 11px; font-weight: 650; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid rgba(148,163,184,.2); }
  td { padding: .45rem .5rem; border-top: 1px solid rgba(148,163,184,.1); color: #e2e8f0; }
  th.num, td.num { text-align: right; white-space: nowrap; }
  select, input { min-height: 34px; padding: .35rem .5rem; border: 1px solid rgba(148,163,184,.2); border-radius: 8px; background: #07101f; color: #e2e8f0; font-size: 13px; }
  button { display: inline-flex; align-items: center; gap: 6px; min-height: 34px; padding: .35rem .7rem; border: 1px solid rgba(148,163,184,.24); border-radius: 8px; color: #cbd5e1; background: rgba(15,23,42,.6); cursor: pointer; font: inherit; font-size: 13px; }
  button:disabled { opacity: .5; cursor: default; }
  button.primary { color: #e0f2fe; border-color: #7dd3fc; background: rgba(14,116,144,.25); }
  .muted { color: #94a3b8; }
  .drift { color: #fda4af; }
  .boundary { margin-top: .6rem; color: #94a3b8; font-size: 11px; line-height: 1.5; }
  .pending { margin-top: .9rem; padding: .8rem; border: 1px solid rgba(251,191,36,.35); border-radius: 10px; background: rgba(251,191,36,.06); }
  .pending table { margin-top: .5rem; }
  .in { color: #6ee7b7; } .out { color: #fda4af; }
`;

// Holder positions are booked money, so the shared style keeps its cents; shares read to two places.
const money = (v: unknown, currency = 'USD') => fmtMoney(v as number | string | null | undefined, currency, 2);
const pct = (v: unknown) => fmtPct(v as number | string | null | undefined, 2);
/** Where the share came from, in the user's words; the raw source stays in the tooltip. */
const sourceWord = (source: string) => /MIGRATION|ONBOARD|REGISTRATION|ACTIVATION/i.test(source) ? 'Onboarding' : humanizeToken(source);

interface Row { portfolioId: string; sharePct: string }

const HoldersPanel: React.FC<Props> = ({ facilityId, currency, canMake, canCheck, onFeedback }) => {
  const canEdit = canMake || canCheck;
  const [model, setModel] = useState<FacilityHolders | null>(null);
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [portfolios, setPortfolios] = useState<Array<{ id: number; portfolioName: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState<LenderShareCandidates | null>(null);
  const [pending, setPending] = useState<HolderChangePending | null>(null);
  const [decisionNote, setDecisionNote] = useState('');

  const load = useCallback(async () => {
    try {
      setModel(await getFacilityHolders(facilityId));
      setPending(await getPendingHolderChange(facilityId).catch(() => null));
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The holders could not be loaded.') });
    }
  }, [facilityId, onFeedback]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!canEdit) return;
    getLenderShareCandidates(facilityId).then(setCandidates).catch(() => setCandidates(null));
  }, [facilityId, canEdit]);

  const adopt = async () => {
    setBusy(true);
    try {
      const next = await adoptLenderSharesAsHolders(facilityId);
      setModel(next);
      onFeedback({ tone: 'success', message: 'The agreement lender shares that name our funds are now the holders; the other lenders remain the syndicate reference.' });
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The lender shares could not be adopted.') });
    } finally {
      setBusy(false);
    }
  };

  const startEdit = async () => {
    try {
      // The portfolio list is served as a page ({content}) since the Blotter paging change (F-MK-063).
      const data = (await axios.get('/api/dimensions/portfolios')).data as
        Array<{ id: number; portfolioName: string }> | { content?: Array<{ id: number; portfolioName: string }> };
      setPortfolios(Array.isArray(data) ? data : (data?.content ?? []));
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'Portfolios could not be listed.') });
      return;
    }
    setRows((model?.holders ?? []).map(h => ({ portfolioId: String(h.portfolioId), sharePct: String(h.sharePct) })));
    setEffectiveFrom('');
    setSourceRef('');
    setEditing(true);
  };
  const total = useMemo(() => rows.reduce((s, r) => s + (Number(r.sharePct) || 0), 0), [rows]);
  const propose = async () => {
    setBusy(true);
    try {
      const next = await proposeHolderChange(facilityId, {
        effectiveFrom: effectiveFrom || undefined,
        sourceRef: sourceRef || undefined,
        holders: rows.filter(r => r.portfolioId).map(r => ({ portfolioId: Number(r.portfolioId), sharePct: Number(r.sharePct) })),
      });
      setPending(next);
      setEditing(false);
      onFeedback({ tone: 'success', message: next.actorMayApprove && !next.gateReason
        ? 'Change proposed. Review the transfers below and approve to apply it.'
        : 'Change proposed; a checker who did not propose it approves it.' });
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The change could not be proposed.') });
    } finally {
      setBusy(false);
    }
  };
  const decide = async (approve: boolean) => {
    setBusy(true);
    try {
      const next = approve ? await approveHolderChange(facilityId, decisionNote.trim() || undefined) : await rejectHolderChange(facilityId, decisionNote.trim());
      setModel(next);
      setPending(null);
      setDecisionNote('');
      onFeedback({ tone: 'success', message: approve
        ? 'Shares changed. The drawn balance moved between the funds at the mark; every later draw, repayment, accrual and mark books by the new shares.'
        : 'Change rejected; the shares are unchanged.' });
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, approve ? 'The change could not be applied.' : 'The change could not be rejected.') });
      void load();
    } finally {
      setBusy(false);
    }
  };

  if (!model) return null;
  return (
    <Card aria-label="Facility holders">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <h2><Users size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />Holders</h2>
          <p className="sub">{model.holders.length === 0 ? 'No fund holds this facility yet; the first holder is written when the commitment is registered.'
            : model.holders.length === 1 ? 'One fund holds the whole facility.'
            : `${model.holders.length} funds hold the facility; every booked amount is split by share to the cent.`}
            {model.drift != null && Number(model.drift) !== 0 && <span className="drift"> Facility drawn differs from the sum of holder positions by {money(model.drift, currency)}.</span>}
          </p>
        </div>
        {canEdit && !editing && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {candidates?.adoptable && <button type="button" disabled={busy} title={candidates.note} onClick={() => void adopt()}><FileSignature size={14} />Adopt agreement lender shares ({candidates.matched.length})</button>}
          {canMake && !pending && <button type="button" onClick={() => void startEdit()}><Pencil size={14} />Change shares</button>}
        </div>}
      </div>

      {!editing && (
        <table>
          <thead><tr><th>Fund</th><th className="num">Share</th><th className="num">Commitment share</th><th className="num">Expected drawn</th><th className="num">Drawn to date (IBOR)</th><th className="num">Market value</th><th>Since</th><th>Source</th></tr></thead>
          <tbody>
            {model.holders.map(h => <tr key={h.portfolioId}>
              <td>{h.portfolioName || `Portfolio ${h.portfolioId}`}</td>
              <td className="num">{pct(h.sharePct)}</td>
              <td className="num">{money(h.commitmentShare ?? h.expectedCommitment)}</td>
              <td className="num muted">{money(h.expectedDrawn)}</td>
              <td className="num">{h.drawnToDate == null ? <span className="muted">no position</span> : money(h.drawnToDate)}</td>
              <td className="num">{money(h.marketValue)}</td>
              <td>{fmtDate(h.effectiveFrom)}</td>
              <td className="muted" title={`${h.source}${h.sourceRef ? ` · ${h.sourceRef}` : ''}`}>{sourceWord(h.source)}{h.sourceRef && !/^V\d+$/i.test(h.sourceRef) ? ` · ${h.sourceRef}` : ''}</td>
            </tr>)}
            {model.holders.length > 1 && <tr><td><strong>Facility</strong></td><td className="num"><strong>100%</strong></td><td className="num"><strong>{money(model.facilityCommitment)}</strong></td><td className="num"><strong>{money(model.facilityDrawn)}</strong></td><td className="num"><strong>{money(model.holderDrawnSum)}</strong></td><td /><td /><td /></tr>}
          </tbody>
        </table>
      )}

      {pending && !editing && (
        <div className="pending" aria-label="Holder change waiting for approval">
          <strong><ArrowRightLeft size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Share change from {fmtDate(pending.effectiveFrom)}, waiting for approval</strong>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Proposed by {pending.proposedBy}{pending.sourceRef ? ` · ${pending.sourceRef}` : ''}. {pending.preview.note}</div>
          <table>
            <thead><tr><th>Fund</th><th className="num">New share</th><th className="num">Holds on {fmtDate(pending.effectiveFrom)}</th><th className="num">Entitled at new share</th><th className="num">Transfer</th></tr></thead>
            <tbody>
              {pending.preview.transfers.map(t => <tr key={t.portfolioId}>
                <td>{t.portfolioCode}</td>
                <td className="num">{pct(t.newSharePct)}</td>
                <td className="num">{money(t.heldOnDate)}</td>
                <td className="num">{money(t.entitled)}</td>
                <td className={`num ${Number(t.transfer) > 0 ? 'in' : Number(t.transfer) < 0 ? 'out' : 'muted'}`}>{Number(t.transfer) === 0 ? '—' : `${Number(t.transfer) > 0 ? 'in ' : 'out '}${money(Math.abs(Number(t.transfer)))}`}</td>
              </tr>)}
              <tr><td><strong>Facility drawn</strong></td><td className="num"><strong>100%</strong></td><td className="num"><strong>{money(pending.preview.drawnOnDate)}</strong></td><td className="num"><strong>{money(pending.preview.drawnOnDate)}</strong></td><td className="num muted">nets to 0</td></tr>
            </tbody>
          </table>
          {canCheck && pending.actorMayApprove && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <input aria-label="Decision note" placeholder="Note (required to reject)" value={decisionNote} onChange={e => setDecisionNote(e.target.value)} style={{ flex: 1, minWidth: 220 }} />
            <button type="button" className="primary" disabled={busy} onClick={() => void decide(true)}><ShieldCheck size={14} />{busy ? 'Applying…' : 'Approve and transfer'}</button>
            <button type="button" disabled={busy || !decisionNote.trim()} onClick={() => void decide(false)}><X size={14} />Reject</button>
          </div>}
          {pending.gateReason && !(canCheck && pending.actorMayApprove) && <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{pending.gateReason}</div>}
        </div>
      )}

      {editing && (
        <div style={{ marginTop: 10 }}>
          <table>
            <thead><tr><th>Fund</th><th className="num">Share %</th><th /></tr></thead>
            <tbody>
              {rows.map((r, i) => <tr key={i}>
                <td><select aria-label={`Holder ${i + 1} fund`} value={r.portfolioId} onChange={e => setRows(rows.map((x, j) => j === i ? { ...x, portfolioId: e.target.value } : x))}>
                  <option value="">Select fund</option>{portfolios.map(p => <option key={p.id} value={p.id}>{p.portfolioName}</option>)}</select></td>
                <td className="num"><input aria-label={`Holder ${i + 1} share`} type="number" step="0.00000001" min="0" max="100" value={r.sharePct} onChange={e => setRows(rows.map((x, j) => j === i ? { ...x, sharePct: e.target.value } : x))} style={{ width: 130, textAlign: 'right' }} /></td>
                <td><button type="button" aria-label="Remove holder" onClick={() => setRows(rows.filter((_, j) => j !== i))}><X size={13} /></button></td>
              </tr>)}
              <tr><td colSpan={3}><button type="button" onClick={() => setRows([...rows, { portfolioId: '', sharePct: '' }])}>Add fund</button></td></tr>
              <tr><td><strong>Total</strong></td><td className="num" style={{ color: Math.abs(total - 100) < 1e-6 ? '#6ee7b7' : '#fda4af' }}><strong>{total.toFixed(4)}%</strong></td><td /></tr>
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, color: '#94a8c2', fontSize: 11 }}>Effective from<input type="date" aria-label="Effective from" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} /></label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, color: '#94a8c2', fontSize: 11 }}>Amendment reference<input type="text" aria-label="Amendment reference" value={sourceRef} onChange={e => setSourceRef(e.target.value)} placeholder="e.g. Amendment No. 2 §3.1" style={{ minWidth: 240 }} /></label>
            <button type="button" className="primary" disabled={busy || Math.abs(total - 100) > 1e-6 || rows.some(r => !r.portfolioId)} onClick={() => void propose()}><Check size={14} />{busy ? 'Proposing…' : 'Propose change'}</button>
            <button type="button" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
      <div className="boundary">{model.authorityBoundary}</div>
    </Card>
  );
};

export default HoldersPanel;
